import { useState } from "react";
import { PlusIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export interface MessageVariable {
  variable: string;
  description: string;
}

/** Valid custom variable name: lowercase, starts with a letter, a-z 0-9 _ (mirrors backend sanitization). */
export const ACTIVATION_VARIABLE_NAME_REGEX = /^[a-z][a-z0-9_]*$/;

/** Matches `{token}` occurrences inside a message. Keep in sync with `renderActivationPreview`. */
export const ACTIVATION_TOKEN_REGEX = /\{([a-z][a-z0-9_]*)\}/g;

/**
 * Custom `{token}`s found in an activation message that aren't built-in
 * variables. These become extra inputs in the approve-creator dialog.
 */
export function extractCustomActivationVariables(message: string): string[] {
  if (!message) return [];
  const builtIn = new Set(ACTIVATION_MESSAGE_VARIABLES.map((v) => v.variable));
  const found: string[] = [];
  for (const match of message.matchAll(ACTIVATION_TOKEN_REGEX)) {
    const name = match[1];
    if (!builtIn.has(name) && !found.includes(name)) {
      found.push(name);
    }
  }
  return found;
}

export const ACTIVATION_MESSAGE_VARIABLES: MessageVariable[] = [
  { variable: "current_tier_order", description: "Current partnership tier order: e.g. 20% Rev Share + Paid: $600" },
  // { variable: "creator_fullname", description: "Creator's full name" },
  { variable: "creator_username", description: "Creator's username" },
  // { variable: "user_name", description: "Your name (sender)" },
  // { variable: "organization_name", description: "Your organization name" },
  // {
  //   variable: "organization_description",
  //   description: "Organization description",
  // },
  // { variable: "organization_website", description: "Organization website URL" },
  // { variable: "campaign_name", description: "Campaign name" },
  // {
  //   variable: "campaign_platform",
  //   description: "Platform (TikTok, Instagram, YouTube)",
  // },
  // {
  //   variable: "partnership",
  //   description: "Partnership details (paid, gifting, amount, etc.)",
  // },
  {
    variable: "promo_code",
    description: "Creator's unique promo code (if configured)",
  },
  {
    variable: "unique_link",
    description: "Creator's unique tracking link (if configured)",
  },
];

const copyVariable = (name: string) => {
  navigator.clipboard
    .writeText(`{${name}}`)
    .then(() => toast.success(`Copied {${name}}`))
    .catch(() => toast.error("Failed to copy to clipboard"));
};

function AddCustomVariable({
  existingNames,
  onCreate,
}: {
  existingNames: string[];
  onCreate: (name: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");

  const normalized = name.trim().toLowerCase().replace(/\s+/g, "_");
  const isValid = ACTIVATION_VARIABLE_NAME_REGEX.test(normalized);
  const isTaken = existingNames.includes(normalized);

  const handleCreate = () => {
    if (!isValid || isTaken) return;
    onCreate(normalized);
    navigator.clipboard
      .writeText(`{${normalized}}`)
      .then(() =>
        toast.success(`Copied {${normalized}} — paste it into your message`),
      )
      .catch(() => toast.error("Failed to copy to clipboard"));
    setName("");
    setOpen(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (!next) setName("");
        setOpen(next);
      }}
    >
      <Tooltip delayDuration={300}>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-auto rounded-sm px-1.5 py-0.5 font-mono text-xs border-dashed"
            >
              <PlusIcon className="size-3" />
              Custom
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent className="max-w-54 text-center text-balance whitespace-pre-wrap">
          <p>Create your own variable, e.g. a password or login email</p>
        </TooltipContent>
      </Tooltip>
      <PopoverContent className="w-80" align="start">
        <div className="flex flex-col gap-2">
          <Label>New custom variable</Label>
          <p className="text-xs text-muted-foreground">
            You'll be asked for its value when approving each creator, and Amy
            will include it in the activation email.
          </p>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleCreate();
              }
            }}
            placeholder="e.g. password"
            className="font-mono text-sm"
            autoFocus
          />
          {name.trim() !== "" && !isValid && (
            <p className="text-xs text-destructive">
              Use lowercase letters, numbers and underscores, starting with a
              letter.
            </p>
          )}
          {isTaken && (
            <p className="text-xs text-destructive">
              {`{${normalized}}`} already exists.
            </p>
          )}
          <div className="flex justify-end">
            <Button
              size="sm"
              onClick={handleCreate}
              disabled={!isValid || isTaken}
            >
              <PlusIcon />
              Copy {normalized && isValid ? `{${normalized}}` : "variable"}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface MessageVariablesProps {
  variables: MessageVariable[];
  className?: string;
  /**
   * Current message content. When provided, custom `{tokens}` found in it are
   * rendered as chips and a "+ Custom" button allows creating new ones.
   */
  message?: string;
}

export function MessageVariables({
  variables,
  className,
  message,
}: MessageVariablesProps) {
  const customVariables =
    message != null ? extractCustomActivationVariables(message) : [];

  // Freshly created via "+" but not yet pasted into the message. Kept visible
  // so the new variable doesn't "disappear" until the user pastes it.
  const [pendingVariables, setPendingVariables] = useState<string[]>([]);
  const visiblePending = pendingVariables.filter(
    (name) => !customVariables.includes(name),
  );

  return (
    <div className={cn("flex flex-wrap items-center gap-1.5", className)}>
      <span className="me-1 text-xs text-muted-foreground">Variables - click to copy</span>
        {variables.map((v) => (
          <Tooltip key={v.variable} delayDuration={300}>
            <TooltipTrigger asChild>
              <Button
                variant="flat"
                size="sm"
                className="h-auto rounded-sm p-0.5 font-mono text-xs"
                onClick={() => copyVariable(v.variable)}
              >
                {`{${v.variable}}`}
              </Button>
            </TooltipTrigger>
            <TooltipContent className="max-w-54 text-center text-balance whitespace-pre-wrap">
              <p>{v.description}</p>
            </TooltipContent>
          </Tooltip>
        ))}
        {customVariables.map((name) => (
          <Tooltip key={name} delayDuration={300}>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-auto rounded-sm px-1.5 py-0.5 font-mono text-xs border-dashed"
                onClick={() => copyVariable(name)}
              >
                {`{${name}}`}
              </Button>
            </TooltipTrigger>
            <TooltipContent className="max-w-54 text-center text-balance whitespace-pre-wrap">
              <p>
                Custom — you'll enter its value when approving each creator
              </p>
            </TooltipContent>
          </Tooltip>
        ))}
        {visiblePending.map((name) => (
          <Tooltip key={name} delayDuration={300}>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-auto rounded-sm px-1.5 py-0.5 font-mono text-xs border-dashed opacity-60"
                onClick={() => copyVariable(name)}
              >
                {`{${name}}`}
              </Button>
            </TooltipTrigger>
            <TooltipContent className="max-w-54 text-center text-balance whitespace-pre-wrap">
              <p>
                Not in your message yet — click to copy, then paste it where
                you want it
              </p>
            </TooltipContent>
          </Tooltip>
        ))}
        {message != null && (
          <AddCustomVariable
            existingNames={[
              ...variables.map((v) => v.variable),
              ...customVariables,
              ...visiblePending,
            ]}
            onCreate={(name) =>
              setPendingVariables((prev) =>
                prev.includes(name) ? prev : [...prev, name],
              )
            }
          />
        )}
    </div>
  );
}
